import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { ar } from "@/i18n/ar";
import { en } from "@/i18n/en";
import { arSA } from "@/i18n/ar-sa";

export type Language = "ar" | "en";
export type Region = "eg" | "sa" | "other";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Translations = Record<string, any>;

interface LanguageContextType {
  lang: Language;
  t: Translations;
  toggleLanguage: () => void;
  dir: "rtl" | "ltr";
  setRegionDialect: (region: Region) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Deep merge: override only keys that exist in the overlay
function deepMerge(base: any, overlay: any): any {
  if (!overlay) return base;
  const result = { ...base };
  for (const key of Object.keys(overlay)) {
    if (
      overlay[key] &&
      typeof overlay[key] === "object" &&
      !Array.isArray(overlay[key]) &&
      base[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], overlay[key]);
    } else {
      result[key] = overlay[key];
    }
  }
  return result;
}

const baseTranslations: Record<Language, Translations> = { ar, en };

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLang] = useState<Language>(() => {
    try {
      return (localStorage.getItem("lang") as Language) || "ar";
    } catch {
      return "ar";
    }
  });

  const [region, setRegion] = useState<Region>(() => {
    try {
      return (localStorage.getItem("detected_region") as Region) || "eg";
    } catch {
      return "eg";
    }
  });

  const toggleLanguage = useCallback(() => {
    setLang((prev) => {
      const next = prev === "ar" ? "en" : "ar";
      try { localStorage.setItem("lang", next); } catch {}
      return next;
    });
  }, []);

  const setRegionDialect = useCallback((r: Region) => {
    setRegion(r);
  }, []);

  // Build translations: if Arabic + Saudi region, merge Saudi overrides
  const t = lang === "ar" && region === "sa"
    ? deepMerge(baseTranslations.ar, arSA)
    : baseTranslations[lang];

  const value: LanguageContextType = {
    lang,
    t,
    toggleLanguage,
    dir: lang === "ar" ? "rtl" : "ltr",
    setRegionDialect,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
